import { useMemo } from 'react';

import config from 'config';

import { useLDFlags } from '../../checkout/utils/LD/useLDFlags';
import { useMembership } from '../../techstyle-shared/react-accounts';
import { useFeature } from '../../techstyle-shared/react-features';
import { useMarketingDisposition } from '../../techstyle-shared/react-marketing';
import { useDomain, useSession } from '../../techstyle-shared/redux-core';
import { staticFeatureValidators } from '../constants/featureFlags';

import useGender from './useGender';

export { default as featureNames } from '../constants/featureFlags';

const defaultOptions = {
  includeValue: false,
};

/**
 * Hook that returns current state of a feature. If it is not yet loaded, it
 * will dispatch `loadFeatures` and update its value after the feature has loaded.
 *
 * This hook returns feature flags in the following priority:
 * 1. Static feature validators
 * 2. LaunchDarkly
 * 3. Feature creature
 *
 * There is an `options` parameter that can be passed to return the value of the feature
 * flag data as well as the enabled state. Passing in `{ includeValue: true }` will change
 * the return value to the following shape: { enabled, data }
 *
 * NOTE: If the feature was not pre-fetched, this update will trigger a re-render
 *  of the component(s) using this hook.
 */
export default function useHasFeature(handle, options = defaultOptions) {
  const session = useSession();
  const { tld } = useDomain();
  const { offer, experience } = useMarketingDisposition();
  const membership = useMembership();
  const { gender } = useGender();
  const additionalQueryParams = {
    brand: config.get('public.brand.name') + tld.substring(1),
    membership: membership.membershipLevelGroup.toLowerCase(),
    gender: gender,
  };
  if (
    additionalQueryParams.membership !== 'vip' &&
    additionalQueryParams.membership !== 'elite'
  ) {
    additionalQueryParams.funnel = offer || 'default';
    additionalQueryParams.experience = experience || 'default';
  }
  const { [handle]: launchDarklyFlagValue } = useLDFlags();
  const { includeValue } = options;

  const {
    isStaticEnabled,
    hasStaticFeature,
    featureCreatureHandle,
    hasLaunchDarklyFeature,
    isEnabledInLaunchDarkly,
  } = useMemo(() => {
    if (launchDarklyFlagValue !== undefined) {
      return {
        isStaticEnabled: false,
        hasLaunchDarklyFeature: true,
        isEnabledInLaunchDarkly: Boolean(launchDarklyFlagValue),
        featureCreatureHandle: undefined,
        hasStaticFeature: false,
      };
    }

    const validator = staticFeatureValidators[handle];
    const hasStaticFeature = handle in staticFeatureValidators;

    if (hasStaticFeature) {
      const isStaticEnabled =
        typeof validator === 'function'
          ? validator({
              handle,
              session,
              membership,
              gender,
              tld,
            })
          : validator;

      return {
        hasStaticFeature,
        isStaticEnabled,
        hasLaunchDarklyFeature: false,
        isEnabledInLaunchDarkly: false,
        featureCreatureHandle: undefined,
      };
    }

    return {
      hasStaticFeature: false,
      isStaticEnabled: false,
      hasLaunchDarklyFeature: false,
      isEnabledInLaunchDarkly: false,
      featureCreatureHandle: handle,
    };
  }, [handle, launchDarklyFlagValue, session, membership, gender, tld]);

  const { isEnabled: isEnabledFeatureCreature, data: featureCreatureData } =
    useFeature(featureCreatureHandle, {
      additionalQueryParams,
    });

  const result = useMemo(() => {
    let enabled;
    let data;

    if (hasLaunchDarklyFeature) {
      enabled = isEnabledInLaunchDarkly;
      data = launchDarklyFlagValue;
    } else if (hasStaticFeature) {
      enabled = isStaticEnabled;
      data = isStaticEnabled;
    } else {
      enabled = isEnabledFeatureCreature;
      data = featureCreatureData?.value ?? false;
    }

    if (includeValue) {
      return {
        enabled,
        data,
      };
    }

    return enabled;
  }, [
    hasLaunchDarklyFeature,
    hasStaticFeature,
    includeValue,
    isEnabledInLaunchDarkly,
    launchDarklyFlagValue,
    isStaticEnabled,
    isEnabledFeatureCreature,
    featureCreatureData,
  ]);

  return result;
}
